import { useState, useEffect, useRef } from "react";
import { ref } from "firebase/storage";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF, STAFF_COLORS } from "../../config/constants";
import { staffAvatar } from "../../utils/styles";

export default function SignatureManager({ theme, staffSigs, onSave, onClear, saving }) {
  const t = THEMES[theme];
  const canvasRef = useRef();
  const [drawing,    setDrawing]    = useState(false);
  const [lastPos,    setLastPos]    = useState(null);
  const [preview,    setPreview]    = useState(null);
  const [saved,      setSaved]      = useState(false);
  const [selected,   setSelected]   = useState(STAFF[0]);

  // When selected staff changes or staffSigs loads, draw saved sig onto canvas
  useEffect(() => {
    const existing = staffSigs[selected];
    if (existing) {
      drawImageOnCanvas(existing);
    } else {
      clearCanvasOnly();
    }
    setPreview(null);
    setSaved(false);
  }, [selected, staffSigs]);

  function clearCanvasOnly() {
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
  }

  function drawImageOnCanvas(src) {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const scale = Math.min(canvas.width / img.width, canvas.height / img.height, 1);
      const w = img.width * scale, h = img.height * scale;
      ctx.drawImage(img, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
    };
    img.src = src;
  }

  function getPos(e) {
    const canvas = canvasRef.current;
    const r = canvas.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (canvas.width / r.width), y: (src.clientY - r.top) * (canvas.height / r.height) };
  }

  function startDraw(e) {
    e.preventDefault();
    setDrawing(true);
    setLastPos(getPos(e));
    setPreview(null);
  }

  function draw(e) {
    e.preventDefault();
    if (!drawing || !lastPos) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath(); ctx.moveTo(lastPos.x, lastPos.y); ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a1a1a"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.lineJoin = "round";
    ctx.stroke();
    setLastPos(pos);
  }

  function endDraw() {
    setDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const off = document.createElement("canvas");
    off.width = canvas.width; off.height = canvas.height;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, off.width, off.height);
    ctx.drawImage(canvas, 0, 0);
    setPreview(off.toDataURL("image/png"));
  }

  function handleClear() {
    clearCanvasOnly();
    setPreview(null);
    setSaved(false);
    onClear(selected);
  }

  async function handleSave() {
    const canvas = canvasRef.current;
    const off = document.createElement("canvas");
    off.width = canvas.width; off.height = canvas.height;
    const ctx = off.getContext("2d");
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, off.width, off.height);
    ctx.drawImage(canvas, 0, 0);
    await onSave(selected, off.toDataURL("image/png"));
    setSaved(true);
    setPreview(null);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:16,padding:24}}>
      <div style={{fontWeight:700,fontSize:15,color:t.text,marginBottom:4}}>Staff Signatures</div>
      <div style={{fontSize:12,color:t.textMuted,marginBottom:20}}>Draw and save each staff member's signature. Used automatically on engagement letter PDFs.</div>

      {/* Staff selector */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <span style={{fontSize:13,color:t.textSub,fontWeight:600,minWidth:60}}>Signing as:</span>
        <div style={{display:"flex",gap:6}}>
          {STAFF.map(name=>(
            <button key={name} onClick={()=>setSelected(name)}
              style={{display:"flex",alignItems:"center",gap:6,padding:"6px 14px",borderRadius:20,
                border:`1px solid ${selected===name?(STAFF_COLORS[name]||BRAND.blue)+"60":t.border}`,
                background:selected===name?(STAFF_COLORS[name]||BRAND.blue)+"15":"transparent",
                cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:selected===name?700:500,
                fontSize:13,color:selected===name?(STAFF_COLORS[name]||BRAND.blue):t.textSub}}>
              {staffAvatar(name,18)}
              {name}
              {staffSigs[name]&&selected!==name&&<span style={{width:6,height:6,borderRadius:"50%",background:BRAND.green,display:"inline-block"}}/>}
            </button>
          ))}
        </div>
      </div>

      {/* Saved indicator */}
      {staffSigs[selected]&&!preview&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"6px 12px",borderRadius:8,
          background:BRAND.green+"12",border:`1px solid ${BRAND.green}30`}}>
          <span style={{width:7,height:7,borderRadius:"50%",background:BRAND.green,display:"inline-block"}}/>
          <span style={{fontSize:12,color:BRAND.green,fontWeight:600}}>{selected}'s signature is saved — drawing will replace it</span>
        </div>
      )}
      {preview&&(
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"6px 12px",borderRadius:8,
          background:"#faeeda",border:"1px solid #fac775"}}>
          <span style={{width:7,height:7,borderRadius:"50%",background:"#f59e0b",display:"inline-block"}}/>
          <span style={{fontSize:12,color:"#854f0b",fontWeight:600}}>Preview — click Save to confirm</span>
        </div>
      )}

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={480} height={140}
        style={{width:"100%",height:140,
          border:`2px dashed ${drawing?STAFF_COLORS[selected]||BRAND.blue:preview?"#f59e0b":t.border}`,
          borderRadius:10,background:"#fff",cursor:"crosshair",touchAction:"none",display:"block",
          transition:"border-color .15s"}}
        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
      />
      <div style={{fontSize:11,color:t.textMuted,marginTop:5,marginBottom:14}}>Sign above using your mouse or finger</div>

      {/* Actions */}
      <div style={{display:"flex",gap:8}}>
        <button onClick={handleSave} disabled={saving[selected]||!preview}
          style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6,
            padding:"9px 0",borderRadius:9,
            background:preview?`linear-gradient(135deg,${BRAND.blue},#1a8bbf)`:"#ccc",
            border:"none",color:"#fff",fontSize:13,fontWeight:700,cursor:preview?"pointer":"not-allowed",
            fontFamily:"'Outfit',sans-serif",opacity:preview?1:0.6}}>
          {saving[selected]?<><MiniSpinner size={12} color="#fff"/>Saving...</>:saved?"✓ Saved!":"Save Signature"}
        </button>
        <button onClick={handleClear}
          style={{padding:"9px 16px",borderRadius:9,background:"transparent",
            border:"1px solid #f8717150",color:"#f87171",fontSize:13,fontWeight:600,
            cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
          Clear
        </button>
      </div>
    </div>
  );
}



// ─── Reports Page ────────────────────────────────────────────────────────────
